import { z } from 'zod';

import { validateJSONString } from '@nw/shared/utils';

import {
  EventsTypeSchema,
  MultiStepFormFieldsDataSchema,
  ValidationSchema
} from '../../shared/types/Schemas.zod';

import {
  ProductExtraDataSchema,
  RegistrationMessagesConfigSchema
} from './ProductExtraDataSchemas.zod';

export const ProductSegmentConfigSchema = EventsTypeSchema.optional();

export const PricingDetailsSchema = z.object({
  total_amount_to_be_paid: z.number(),
  total_amount_to_be_paid_in_least_subunit: z.number(),
  emi_installment_details: z.null(),
  customizable_installment_details: z.null()
});

export const ProductPurchaseDetailsForUserSchema = z.object({
  applicable_discounts: z.array(z.string()),
  change_program_details: z.null(),
  pricing_details: PricingDetailsSchema
});

export const BrandingSectionSchema = z.object({
  branding_section_markdown_content: z.string()
});

export const LayoutTypeEnum = z.enum([
  'FORM_SECTION_LEFT_BRANDING_SECTION_RIGHT',
  'ONLY_FORM_SECTION',
  'FORM_SECTION_RIGHT_BRANDING_SECTION_LEFT'
]);

export const ProductInfoSchema = z.object({
  layout: z.object({
    type: LayoutTypeEnum
  }),
  form_section_config: z.object({
    success_page_config: z.string().nullable(),
    form_header_logo_url: z.string().nullable()
  }),
  branding_section_config: z
    .string()
    .refine(validateJSONString(BrandingSectionSchema, false)),
  custom_styles: z.string(),
  custom_global_styles: z.string().optional()
});

export const BaseProductSchema = z.object({
  amount: z.number(),
  referral_discount: z.number(),
  paid_customers_discount: z.number(),
  advance_paid: z.number(),
  already_paid_other_than_advance: z.number(),
  advance_paid_in_least_subunit: z.number(),
  already_paid_other_than_advance_in_least_subunit: z.number(),
  product_code: z.string(),
  extra_data: z
    .string()
    .refine(validateJSONString(ProductExtraDataSchema, false)),
  is_active: z.boolean(),
  user_extra_info_config: z
    .string()
    .refine(validateJSONString(MultiStepFormFieldsDataSchema, false)),
  product_info: z.string().nullable(),
  product_config: z
    .string()
    .refine(validateJSONString(ProductSegmentConfigSchema, false)),
  program: z.string(),
  program_extra_info: z.string(),
  currency: z.string(),
  dependent_product_code: z.string(),
  dependent_product_code_condition: z.string(),
  allowed_no_of_purchases: z.number(),
  program_info: z.string(),
  program_config: z.string(),
  program_image_url: z.string(),
  program_subscription_url: z.string(),
  error_messages_config: z.string(),
  registration_messages_config: z
    .string()
    .refine(validateJSONString(RegistrationMessagesConfigSchema, false)),
  tnc_to_accept: z.array(z.string()),
  product_purchase_details_for_user: ProductPurchaseDetailsForUserSchema,
  coupon_discount: z.number(),
  coupon_discount_in_least_subunit: z.number(),
  covid_discount_amount_in_least_subunit: z.number(),
  refund_product_discount_amount_in_least_subunit: z.number(),
  original_amount_in_least_subunit: z.number(),
  covid_discount_amount: z.number(),
  refund_product_discount_amount: z.number(),
  original_amount: z.number(),
  amount_in_least_subunit: z.number(),
  product_details: z.string()
});

export const PricingTypeEnum = z.enum(['FULL_PAYMENT']);

export const ProductDetailsSchema = z.object({
  pricing_type: PricingTypeEnum,
  product_price: z.number(),
  installment_pricing_details: z.null(),
  applicable_discounts: z.array(z.string())
});

export const DefaultProductSchema = BaseProductSchema.extend({
  product_details: ProductDetailsSchema
});

export const ErrorMessageConfigSchema = z.object({
  payment_error_messages: ValidationSchema.optional()
});

export const PreviousProductSchema = z.object({
  product_code: z.string(),
  amount_paid_by_user: z.string(),
  is_advance_product: z.boolean(),
  product_display_name: z.string()
});
